/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2024.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, {useContext} from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import QContext from "QContext";
import {useRowEditContext} from "./RowEditContext";

interface Props
{
   rowId: string | number;
   /** Whether this row CAN be edited (table-level permission, plus for child-list: parent is in view mode). */
   isRowEditable: boolean;
   /** Fires when the pencil is clicked on a row with no edit in progress. */
   onEnterEdit: (rowId: string | number) => void;
   /** When the row can't be edited because of an ambient condition (e.g. parent record is in edit mode),
    *  providing a message here causes a disabled pencil to render with that text as its tooltip.
    *  Omit to keep the overlay hidden entirely. */
   disabledReason?: string;
   /** Whether this row can be deleted — renders a trash icon after the pencil. */
   isRowDeletable?: boolean;
   /** Fires when the trash is clicked on a row with no edit in progress. */
   onDeleteClick?: (rowId: string | number) => void;
   /** When the row can't be deleted because of an ambient condition (e.g. parent record is in
    *  edit mode), providing a message here causes a disabled trash to render with that text as
    *  its tooltip — mirroring disabledReason's behavior for the pencil. */
   deleteDisabledReason?: string;
}

export const EditRowActions: React.FC<Props> = ({rowId, isRowEditable, onEnterEdit, disabledReason, isRowDeletable, onDeleteClick, deleteDisabledReason}) =>
{
   const ctx = useRowEditContext();
   const {accentColor} = useContext(QContext);

   if (!isRowEditable && !isRowDeletable)
   {
      if (disabledReason || deleteDisabledReason)
      {
         return (
            <Box sx={{display: "flex", gap: "4px"}}>
               {disabledReason &&
                  <Tooltip title={disabledReason}>
                     <span className="qqq-row-edit-pencil">
                        <IconButton size="small" disabled>
                           <Icon>edit</Icon>
                        </IconButton>
                     </span>
                  </Tooltip>}
               {deleteDisabledReason &&
                  <Tooltip title={deleteDisabledReason}>
                     <span className="qqq-row-delete-trash">
                        <IconButton size="small" disabled>
                           <Icon>delete</Icon>
                        </IconButton>
                     </span>
                  </Tooltip>}
            </Box>
         );
      }
      return null;
   }

   const isEditingThisRow = ctx?.editingRowId === rowId;
   const isEditingOtherRow = ctx?.editingRowId != null && !isEditingThisRow;

   const handleSaveClick = (e: React.MouseEvent) =>
   {
      e.stopPropagation();
      ctx!.requestSave();
   };

   const handleCancelClick = (e: React.MouseEvent) =>
   {
      e.stopPropagation();
      ctx!.requestCancel();
   };

   const handlePencilClick = (e: React.MouseEvent) =>
   {
      e.stopPropagation();
      onEnterEdit(rowId);
   };

   const handleTrashClick = (e: React.MouseEvent) =>
   {
      e.stopPropagation();
      onDeleteClick?.(rowId);
   };

   if (isEditingThisRow)
   {
      const isSaving = ctx!.isSaving;
      const saveDisabled = isSaving || ctx!.rowBannerState.kind === "formDisabled";

      ////////////////////////////////////////////////////////////////////////////////
      // While the save is in flight, replace BOTH buttons with a single spinner —  //
      // the user can't cancel an in-progress save and shouldn't be able to double- //
      // click it either.                                                           //
      ////////////////////////////////////////////////////////////////////////////////
      if (isSaving)
      {
         return (
            <Box
               className="qqq-row-edit-saving-spinner"
               sx={{
                  display:         "flex",
                  alignItems:      "center",
                  justifyContent:  "center",
                  backgroundColor: "#ffffff",
                  borderRadius:    "6px",
                  boxShadow:       "0 1px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)",
                  padding:         "4px 14px",
               }}
            >
               <CircularProgress size={22} />
            </Box>
         );
      }

      return (
         <Box sx={{display: "flex", gap: "4px"}}>
            <Tooltip title={saveDisabled ? "Cannot save — see message" : "Save"}>
               <span>
                  <IconButton
                     size="small"
                     disabled={saveDisabled}
                     onClick={handleSaveClick}
                     className="qqq-row-edit-save"
                     sx={{
                        backgroundColor: "success.main",
                        color:           "#ffffff",
                        "&:hover":       {backgroundColor: "success.main", filter: "brightness(0.92)"},
                     }}
                  >
                     <Icon sx={{color: "#ffffff"}}>check</Icon>
                  </IconButton>
               </span>
            </Tooltip>
            <Tooltip title="Cancel">
               <IconButton
                  size="small"
                  onClick={handleCancelClick}
                  className="qqq-row-edit-cancel"
                  sx={{
                     backgroundColor: "error.main",
                     color:           "#ffffff",
                     "&:hover":       {backgroundColor: "error.main", filter: "brightness(0.92)"},
                  }}
               >
                  <Icon sx={{color: "#ffffff"}}>close</Icon>
               </IconButton>
            </Tooltip>
         </Box>
      );
   }

   if (isEditingOtherRow)
   {
      return (
         <Box sx={{display: "flex", gap: "4px"}}>
            {isRowEditable &&
               <Tooltip title="Only one row can be edited at a time">
                  <span className="qqq-row-edit-pencil">
                     <IconButton size="small" disabled>
                        <Icon>edit</Icon>
                     </IconButton>
                  </span>
               </Tooltip>}
            {isRowDeletable &&
               <Tooltip title="A row cannot be deleted while another row is being edited">
                  <span className="qqq-row-delete-trash">
                     <IconButton size="small" disabled>
                        <Icon>delete</Icon>
                     </IconButton>
                  </span>
               </Tooltip>}
         </Box>
      );
   }

   //////////////////////////////////////////////////////////////////////////////
   // Normal state: pencil (if editable) + trash (if deletable), accent color. //
   // A deletable-but-not-editable row with an ambient edit-disabled reason    //
   // still shows the disabled pencil alongside the live trash, so the "why    //
   // can't I edit" explainer isn't lost.                                      //
   //////////////////////////////////////////////////////////////////////////////
   return (
      <Box sx={{display: "flex", gap: "4px"}}>
         {isRowEditable &&
            <Tooltip title="Edit row">
               <IconButton size="small" onClick={handlePencilClick} className="qqq-row-edit-pencil">
                  <Icon sx={{color: accentColor}}>edit</Icon>
               </IconButton>
            </Tooltip>}
         {!isRowEditable && disabledReason &&
            <Tooltip title={disabledReason}>
               <span className="qqq-row-edit-pencil">
                  <IconButton size="small" disabled>
                     <Icon>edit</Icon>
                  </IconButton>
               </span>
            </Tooltip>}
         {isRowDeletable &&
            <Tooltip title="Delete row">
               <span className="qqq-row-delete-trash">
                  <IconButton size="small" onClick={handleTrashClick}>
                     <Icon sx={{color: accentColor}}>delete</Icon>
                  </IconButton>
               </span>
            </Tooltip>}
         {!isRowDeletable && deleteDisabledReason &&
            <Tooltip title={deleteDisabledReason}>
               <span className="qqq-row-delete-trash">
                  <IconButton size="small" disabled>
                     <Icon>delete</Icon>
                  </IconButton>
               </span>
            </Tooltip>}
      </Box>
   );
};
